import { initializeVault } from '@/utils/crypto/vaultCrypto';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { useAuthStore } from '@/store/auth.store';
import { vaultService } from '@/services/vault.service';
import type { User } from '@/types/auth.types';

/**
 * [AUTO-INIT VAULT AU LOGIN 2026-07] Initialise le coffre en attente d'un
 * utilisateur qui vient de se connecter, avec le mot de passe qu'il vient de
 * saisir — sans aucune action de sa part.
 *
 * Pourquoi : la décision « plus aucun salarié ne doit être bloqué » (8227bcc,
 * 10/05/2026) supprimait la re-saisie du mot de passe... mais UNIQUEMENT à
 * l'inscription. Un compte existant dont le coffre repasse en attente était
 * toujours renvoyé vers /vault/initialize/:id, qui redemande un mot de passe.
 * Deux conséquences :
 *  - le salarié se retrouve bloqué sur une étape qu'il ne comprend pas
 *    (cas LOVISA / SAM'AUTO, à l'origine de la décision de mai) ;
 *  - le mot de passe saisi peut différer de celui de la connexion, et le coffre
 *    devient alors indéchiffrable alors que le login fonctionne
 *    (cas Pierre LEGAYE / ARCHI PARTNERS, juin-juillet 2026).
 *
 * En dérivant la clé du mot de passe de connexion, les deux ne peuvent plus
 * diverger : le bug disparaît à la racine.
 *
 * Ne jette JAMAIS : en cas d'échec, on laisse le parcours manuel
 * (/vault/initialize/:id) prendre le relais — l'utilisateur reste connecté.
 *
 * @returns true si le coffre a été initialisé, false sinon.
 */
export async function autoInitPendingVault(
  user: User | null | undefined,
  password: string,
): Promise<boolean> {
  const vaultId = user?.pendingVaultId;
  if (!vaultId || !password) {
    return false;
  }

  try {
    const { masterKey, initData } = await initializeVault(password);
    await vaultService.userInitializeVault(vaultId, initData);

    // Clé en mémoire (auto-lock 30 min) : les documents sont lisibles
    // immédiatement, sans redemander le mot de passe.
    useVaultKeyStore.getState().unlockVault(vaultId, masterKey);

    // Le coffre n'est plus en attente : évite la redirection de ProtectedRoute.
    const { user: current, setUser } = useAuthStore.getState();
    if (current) {
      setUser({ ...current, pendingVaultId: null });
    }

    return true;
  } catch (error) {
    // Cas dégradé : on ne bloque pas la connexion, la page d'initialisation
    // classique reste accessible.
    console.error('[autoInitPendingVault] Auto-init du coffre a échoué:', error);
    return false;
  }
}
